import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Edit, Trash2, Archive, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

// UI Components
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

import { canArchiveProcedure } from "../../utils/procedure-permissions";

// Types
import { Procedure, User, Department, TrainingPlan } from '../../types';

import { Can } from "../Can";
import { CanAny } from "../CanAny";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";

interface ProcedureActionsProps {
    procedure: Procedure;
    showArchived: boolean;
    departments: Department[];
    users: User[];
    trainingPlans: TrainingPlan[];
    updateProcedure: (id: string, data: Partial<Procedure>, showToast: boolean) => void;
    archiveProcedure: (id: string, userId: string, reason: string) => void;
    unarchiveProcedure: (id: string, userId: string) => void;
    currentUser: User | null;
    isViewerOnly?: boolean;
    variant?: "outline" | "ghost";
    detailPathPrefix?: string;
}

const getUserId = (userOrId: string | User | null | undefined): string => {
    if (!userOrId) return "";
    return typeof userOrId === 'object' ? userOrId?.id : userOrId;
};

const canDeleteProcedureCheck = (procedureId: string, trainingPlans: TrainingPlan[]) => {
    const hasTrainingPlanHistory = trainingPlans.some(plan => plan.procedures.filter(proc => proc.id == procedureId).length > 0);
    return { canDelete: !hasTrainingPlanHistory };
};

export const ProcedureActions = ({
    procedure,
    showArchived,
    departments,
    users,
    trainingPlans,
    updateProcedure,
    archiveProcedure,
    unarchiveProcedure,
    currentUser,
    isViewerOnly = false,
    variant = "outline",
    detailPathPrefix = '/procedure'
}: ProcedureActionsProps) => {
    const [isArchiving, setIsArchiving] = useState(false);
    const [archiveReason, setArchiveReason] = useState("");

    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState("");

    const [isUnarchiving, setIsUnarchiving] = useState(false);

    const handleArchive = () => {
        if (!currentUser) return;
        archiveProcedure(procedure.id, currentUser.id, archiveReason);
        setIsArchiving(false);
        setArchiveReason("");
    };

    const handleUnarchive = () => {
        if (!currentUser) return;
        unarchiveProcedure(procedure.id, currentUser.id);
        setIsUnarchiving(false);
    };

    const handleDelete = () => {
        if (!currentUser) return;
        updateProcedure(procedure.id, { deleted: true, deletedBy: currentUser.id }, false);
        toast.success("Procedure deleted successfully");
        setIsDeleting(false);
    };

    if (isViewerOnly) return null;

    return (
        <div className="flex justify-end gap-1">
            <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE}>
                {!showArchived ? (
                    <>
                        <CanAny of={[
                            { I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE },
                            { I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE_I_OWN }
                        ]}>
                            <Link to={`${detailPathPrefix}/${procedure.id}/edit`}>
                                <Button
                                    variant={variant}
                                    size="icon"
                                >
                                    <Edit size={variant === "ghost" ? 18 : 16} />
                                </Button>
                            </Link>
                        </CanAny>

                        <Can I={ACTIONS.ARCHIVE} a={RESOURCES.PROCEDURE}>
                            <Dialog open={isArchiving} onOpenChange={setIsArchiving}>
                                <DialogTrigger asChild><Button variant={variant} size="icon"><Archive size={variant === "ghost" ? 18 : 16} /></Button></DialogTrigger>
                                <DialogContent>
                                    <DialogHeader><DialogTitle>Archive Procedure</DialogTitle></DialogHeader>
                                    {(() => {
                                        const { canArchive, activePlans } = canArchiveProcedure(procedure, trainingPlans);
                                        if (!canArchive) return (
                                            <div>
                                                <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4" role="alert">
                                                    <p className="font-bold">Cannot Archive</p>
                                                    <p>This procedure is used in active training plans: {activePlans.map(p => p.trainingPlanName).join(', ')}</p>
                                                </div>
                                                <DialogFooter className="mt-4"><DialogClose asChild><Button variant="outline">Close</Button></DialogClose></DialogFooter>
                                            </div>
                                        );
                                        return (
                                            <>
                                                <DialogDescription>Are you sure you want to archive "{procedure.procedureName}"?</DialogDescription>
                                                <Textarea placeholder="Reason for archiving (optional)..." value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)} />
                                                <DialogFooter>
                                                    <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                                                    <Button onClick={handleArchive}>Archive</Button>
                                                </DialogFooter>
                                            </>
                                        );
                                    })()}
                                </DialogContent>
                            </Dialog>
                        </Can>

                        <Can I={ACTIONS.DELETE} a={RESOURCES.PROCEDURE}>
                            <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
                                <DialogTrigger asChild><Button variant={variant} size="icon" className="text-destructive"><Trash2 size={variant === "ghost" ? 18 : 16} /></Button></DialogTrigger>
                                <DialogContent>
                                    <DialogHeader><DialogTitle>Delete Procedure</DialogTitle></DialogHeader>
                                    {(() => {
                                        const { canDelete } = canDeleteProcedureCheck(procedure.id, trainingPlans);
                                        if (!canDelete) return (
                                            <div>
                                                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
                                                    <p className="font-bold">Cannot Delete</p>
                                                    <p>This procedure has historical data linked to training plans and cannot be deleted. Please archive it instead.</p>
                                                </div>
                                                <DialogFooter className="mt-4"><DialogClose asChild><Button variant="outline">Close</Button></DialogClose></DialogFooter>
                                            </div>
                                        );
                                        return (
                                            <>
                                                <DialogDescription>This action cannot be undone. Type "delete" to confirm.</DialogDescription>
                                                <Input placeholder="delete" value={deleteConfirmation} onChange={(e) => setDeleteConfirmation(e.target.value)} />
                                                <DialogFooter>
                                                    <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                                                    <Button variant="destructive" onClick={handleDelete} disabled={deleteConfirmation !== 'delete'}>Delete</Button>
                                                </DialogFooter>
                                            </>
                                        );
                                    })()}
                                </DialogContent>
                            </Dialog>
                        </Can>
                    </>
                ) : (
                    <Can I={ACTIONS.UNARCHIVE} a={RESOURCES.PROCEDURE}>
                        <Dialog open={isUnarchiving} onOpenChange={setIsUnarchiving}>
                            <DialogTrigger asChild><Button variant={variant} size="icon"><RefreshCw size={variant === "ghost" ? 18 : 16} /></Button></DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Unarchive Procedure</DialogTitle></DialogHeader>
                                <DialogDescription>This will restore "{procedure.procedureName}" to an active state.</DialogDescription>
                                <DialogFooter>
                                    <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                                    <Button onClick={handleUnarchive}>Unarchive</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </Can>
                )}
            </Can>
        </div>
    );
};
